import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Check, ChevronLeft, ChevronRight, Code2, Copy, Pause, Play, RotateCcw } from 'lucide-react';
import type { CodeExample, CodeLanguage } from '../content/types';

const LANGUAGES: CodeLanguage[] = ['Java', 'Python', 'JavaScript'];

export function CodeAnimator({ example }: { example: CodeExample }) {
  const [language, setLanguage] = useState<CodeLanguage>(() => {
    const saved = localStorage.getItem('iprep_code_language') as CodeLanguage | null;
    return saved && LANGUAGES.includes(saved) ? saved : 'Java';
  });
  const variant = example.variants.find((item) => item.language === language) ?? example.variants[0];
  const lines = useMemo(() => variant.code.split('\n'), [variant.code]);
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!playing) return;
    const timer = window.setTimeout(() => {
      if (step >= example.steps.length - 1) setPlaying(false);
      else setStep(step + 1);
    }, 1900);
    return () => window.clearTimeout(timer);
  }, [playing, step, example.steps.length]);

  function chooseLanguage(next: CodeLanguage) {
    setLanguage(next);
    localStorage.setItem('iprep_code_language', next);
    setStep(0);
    setPlaying(false);
  }

  async function copy() {
    await navigator.clipboard.writeText(variant.code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  }

  const chunk = Math.ceil(lines.length / example.steps.length);
  const focusStart = Math.min(step * chunk, lines.length - 1);
  const focusEnd = step === example.steps.length - 1 ? lines.length : Math.min(lines.length, focusStart + chunk);
  const trace = example.traces[Math.min(step, example.traces.length - 1)];

  return (
    <div className="code-animator code-animator-v2">
      <header>
        <div className="window-dots"><i /><i /><i /></div>
        <span>{example.title}</span>
        <div className="code-header-actions">
          <label className="language-picker"><Code2 /><span>Language</span><select value={language} onChange={(event) => chooseLanguage(event.target.value as CodeLanguage)}>{LANGUAGES.map((item) => <option key={item}>{item}</option>)}</select></label>
          <button onClick={copy} aria-label="Copy code">{copied ? <Check /> : <Copy />}</button>
        </div>
      </header>
      <div className="code-body">
        <div className="code-pane">
          <div className="execution-label"><span className={playing ? 'running' : ''} /> {playing ? 'Running' : 'Ready'} · {language}</div>
          <pre>{lines.map((line, index) => {
            const current = index >= focusStart && index < focusEnd;
            const executed = index < focusStart;
            return <code key={`${language}-${index}`} className={`${current ? 'current' : ''} ${executed ? 'executed' : ''}`}><b>{current && index === focusStart ? <motion.i layoutId="execution-cursor">▶</motion.i> : String(index + 1).padStart(2, '0')}</b><span>{highlight(line)}</span></code>;
          })}</pre>
        </div>
        <aside className="code-explain">
          <div className="explain-top"><span>EXECUTION {String(step + 1).padStart(2, '0')}</span><small>{step + 1} / {example.steps.length}</small></div>
          <AnimatePresence mode="wait">
            <motion.div className="trace-block" key={step} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}>
              <h4>{trace.label}</h4>
              <div className="trace-values">{Object.entries(trace.values).map(([name, value]) => <div key={name}><span>{name}</span><strong>{value}</strong></div>)}</div>
              <p>{example.steps[step]}</p>
            </motion.div>
          </AnimatePresence>
        </aside>
      </div>
      <footer className="code-player">
        <button className="player-button" onClick={() => { setStep(0); setPlaying(false); }} aria-label="Restart"><RotateCcw /></button>
        <button className="player-button" onClick={() => { setStep(Math.max(0, step - 1)); setPlaying(false); }} disabled={step === 0} aria-label="Previous step"><ChevronLeft /></button>
        <button className="play-button" onClick={() => { if (step === example.steps.length - 1) setStep(0); setPlaying(!playing); }}>{playing ? <Pause fill="currentColor" /> : <Play fill="currentColor" />}{playing ? 'Pause execution' : 'Run code'}</button>
        <button className="player-button" onClick={() => { setStep(Math.min(example.steps.length - 1, step + 1)); setPlaying(false); }} disabled={step === example.steps.length - 1} aria-label="Next step"><ChevronRight /></button>
        <div className="execution-timeline">{example.steps.map((_, index) => <button key={index} className={index < step ? 'done' : index === step ? 'active' : ''} onClick={() => { setStep(index); setPlaying(false); }}><i /></button>)}</div>
      </footer>
    </div>
  );
}

function highlight(line: string) {
  const keywordWords = 'public|private|protected|static|class|return|if|else|for|while|new|function|const|let|def|in|not|or|and|import|from';
  const typeWords = 'int|boolean|void|String|Integer|Map|List|Set|Queue|Deque|HashMap|ArrayDeque|ListNode|TreeNode|None|True|False|null|number|string';
  const combined = new RegExp(`(\\b(?:${keywordWords})\\b|\\b(?:${typeWords})\\b|"[^"]*"|'[^']*'|\\b\\d+\\b)`, 'g');
  return <>{line.split(combined).filter(Boolean).map((part, index) => {
    if (new RegExp(`^(?:${keywordWords})$`).test(part)) return <em key={index}>{part}</em>;
    if (new RegExp(`^(?:${typeWords})$`).test(part) || /^\d+$/.test(part)) return <mark key={index}>{part}</mark>;
    if (/^["']/.test(part)) return <u key={index}>{part}</u>;
    return part;
  })}</>;
}
